import { Box, Typography, useTheme } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

/**
 * Metric definitions for the comparison table.
 * higherIsBetter: true for all because maxDrawdown is stored as a negative number
 * (-13.32 > -15.67, so the higher stored value = smaller drawdown = better).
 */
const METRICS = [
  { key: "periodReturn", label: "TOTAL RETURN", subLabel: "PERIOD", format: "percent", higherIsBetter: true },
  { key: "relativeReturn", label: "RELATIVE RETURN", subLabel: "VS OTHER", format: "percent", computed: true },
  { key: "annualizedReturn", label: "ANNUALIZED", subLabel: "RETURN", format: "annualized", higherIsBetter: true },
  { key: "sharpeRatio", label: "SHARPE", subLabel: "RATIO", format: "decimal3", higherIsBetter: true },
  { key: "sortinoRatio", label: "SORTINO", subLabel: "RATIO", format: "decimal3", higherIsBetter: true },
  { key: "maxDrawdown", label: "MAX DRAWDOWN", subLabel: "", format: "percent", higherIsBetter: true },
  { key: "twr", label: "TWR", subLabel: "", format: "percent", higherIsBetter: true },
];

const formatValue = (value, format) => {
  if (value == null) return "-";
  switch (format) {
    case "percent":
      return `${value >= 0 ? "+" : ""}${Number(value).toFixed(2)}%`;
    case "annualized":
      // annualizedReturn may already be a percent value (e.g. 46.3) rather than a decimal
      return `${value >= 0 ? "+" : ""}${Number(value).toFixed(1)}%`;
    case "decimal3":
      return Number(value).toFixed(3);
    default:
      return String(value);
  }
};

const formatDelta = (delta, format) => {
  if (delta == null) return null;
  const sign = delta >= 0 ? "+" : "";
  switch (format) {
    case "percent":
      return `${sign}${Math.abs(delta).toFixed(2)}%`;
    case "annualized":
      return `${sign}${Math.abs(delta).toFixed(1)}%`;
    case "decimal3":
      return `${sign}${Math.abs(delta).toFixed(2)}`;
    default:
      return `${sign}${delta}`;
  }
};

/**
 * For each scoreable metric, find the strategy with the best value.
 * Returns a map of { strategyId → winCount } and winner per metric key.
 */
const computeWinners = (strategies) => {
  const winnerPerMetric = {};
  const scoreable = METRICS.filter((metric) => !metric.computed);

  scoreable.forEach(({ key, higherIsBetter }) => {
    const valid = strategies
      .map((strategy) => ({ id: strategy.idStrategy, value: strategy[key] }))
      .filter(({ value }) => value != null);

    if (valid.length === 0) return;

    const winner = valid.reduce((best, current) =>
      higherIsBetter ? (current.value > best.value ? current : best) : (current.value < best.value ? current : best)
    );
    winnerPerMetric[key] = winner.id;
  });

  return winnerPerMetric;
};

/**
 * UnifiedPerformanceTable
 *
 * Renders all strategies as rows and all metrics as columns.
 * Designed for N strategies — no hardcoded row count.
 */
const UnifiedPerformanceTable = ({ strategies }) => {
  const theme = useTheme();

  if (strategies.length < 2) return null;

  const winnerPerMetric = computeWinners(strategies);

  // Relative return: backend provides it for compare strategies; main gets the negated value
  const getRelativeReturn = (strategy, strategyIndex) => {
    if (strategyIndex === 0) {
      const firstCompareRelativeReturn = strategies[1]?.relativeReturn;
      if (firstCompareRelativeReturn == null) return null;
      return -firstCompareRelativeReturn;
    }
    return strategy.relativeReturn ?? null;
  };

  const STRATEGY_COLORS = [
    theme.palette.primary.main,
    "#ff6b6b",
    "#ffd93d",
    "#6bcb77",
    "#4d96ff",
    "#ff922b",
  ];

  const headerCellSx = {
    p: 1.5,
    textAlign: "right",
    borderBottom: `1px solid ${theme.palette.divider}`,
    minWidth: 110,
  };

  const cellSx = {
    px: 1.5,
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    textAlign: "right",
    borderBottom: `1px solid ${theme.palette.divider}`,
    verticalAlign: "top",
  };

  return (
    <Box>
      <Typography variant="overline" fontWeight={700} letterSpacing={2} color="text.secondary" mb={2} display="block">
        UNIFIED PERFORMANCE COMPARISON
      </Typography>

      <Box sx={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...headerCellSx, textAlign: "left", minWidth: 140 }}>
                <Typography variant="overline" color="text.secondary">STRATEGY</Typography>
              </th>
              {METRICS.map((metric) => (
                <th key={metric.key} style={headerCellSx}>
                  <Typography variant="overline" color="text.secondary" display="block">
                    {metric.label}
                  </Typography>
                  {metric.subLabel && (
                    <Typography variant="overline" color="text.disabled" fontSize="0.6rem" display="block">
                      {metric.subLabel}
                    </Typography>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {strategies.map((strategy, strategyIndex) => {
              const relativeReturn = getRelativeReturn(strategy, strategyIndex);
              const otherStrategy = strategyIndex === 0 ? strategies[1] : strategies[0];

              return (
                <tr key={strategy.idStrategy} style={{ paddingTop: 2, }}>
                  {/* Strategy name cell */}
                  <td style={{ ...cellSx, textAlign: "left" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: STRATEGY_COLORS[strategyIndex] ?? "grey.500",
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" fontWeight={500}>
                        {strategy.title}
                      </Typography>
                    </Box>
                  </td>

                  {METRICS.map((metric) => {
                    const isWinner = winnerPerMetric[metric.key] === strategy.idStrategy;

                    if (metric.computed && metric.key === "relativeReturn") {
                      const sign = relativeReturn != null && relativeReturn >= 0 ? "+" : "";
                      const displayValue = relativeReturn != null
                        ? `${sign}${Number(relativeReturn).toFixed(2)}%`
                        : "-";
                      const color = relativeReturn != null
                        ? relativeReturn >= 0 ? "success.main" : "error.main"
                        : "text.secondary";

                      return (
                        <td key={metric.key} style={cellSx}>
                          <Typography variant="body2" color={color} fontWeight={600}>
                            {displayValue}
                          </Typography>
                        </td>
                      );
                    }

                    const rawValue = strategy[metric.key];
                    const displayValue = formatValue(rawValue, metric.format);

                    const otherRawValue = otherStrategy?.[metric.key];
                    const delta = rawValue != null && otherRawValue != null ? rawValue - otherRawValue : null;
                    const deltaDisplay = formatDelta(delta, metric.format);
                    const deltaColor = delta != null
                      ? (metric.higherIsBetter ? (delta >= 0 ? "success.main" : "error.main") : (delta <= 0 ? "success.main" : "error.main"))
                      : "text.disabled";

                    return (
                      <td key={metric.key} style={cellSx}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                          {isWinner && (
                            <TrendingUpIcon sx={{ fontSize: 14, color: "warning.main" }} />
                          )}
                          <Typography variant="body2" fontWeight={isWinner ? 700 : 400}>
                            {displayValue}
                          </Typography>
                        </Box>
                        {deltaDisplay && otherStrategy && (
                          <Typography variant="caption" color={deltaColor} display="block" textAlign="right">
                            {deltaDisplay} {otherStrategy.title}
                          </Typography>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

export default UnifiedPerformanceTable;
